/**
 * External API Client
 * Fetches stock data from backend API using product SKUs
 *
 * API Endpoint: https://nets4you-stock-node-api-stage.onrender.com/api/v1/products/getProductInStockQtyAndPendingOrderCount
 * Producttion:
 * https://nets4you-stock-node-api-production.onrender.com/api/v1/products/getProductInStockQtyAndPendingOrderCount
 * Request Body: { "product_ids": ["SKU1", "SKU2", ...] }
 * Response: { status: true, message: "Success", data: [...] }
 */

const _apiClientLog = typeof window.createLogger === 'function'
  ? window.createLogger('ExternalApiClient')
  : { log: () => {}, warn: () => {}, error: () => {} };

class ExternalApiClient {
  constructor(options = {}) {
    this.apiEndpoint = 'v1/products/getProductInStockQtyAndPendingOrderCount';
    this.cacheEnabled = options.cacheEnabled !== false;
    this.cacheDuration = options.cacheDuration || 5 * 60 * 1000; // 5 minutes default
    this.cache = new Map();
    this.cacheTimestamps = new Map();
    this.baseUrl = 'https://nets4you-stock-node-api-production.onrender.com/api/';
  }

  /**
   * Show a simple page-level loader while API requests are in progress
   */
  _showLoader() {
    try {
      const existing = document.getElementById('cart-backorder-loader');
      if (existing) return;

      // Inject spinner keyframes/style if needed
      if (!document.getElementById('cart-backorder-loader-style')) {
        const style = document.createElement('style');
        style.id = 'cart-backorder-loader-style';
        style.textContent = `
          @keyframes nfy-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .nfy-spinner { width: 40px; height: 40px; border: 4px solid rgba(0,0,0,0.08); border-top-color: #111827; border-radius: 50%; animation: nfy-spin 1s linear infinite; }
        `;
        document.head.appendChild(style);
      }

      const container = document.createElement('div');
      container.id = 'cart-backorder-loader';
      container.setAttribute('aria-hidden', 'false');
      container.style.position = 'fixed';
      container.style.left = '0';
      container.style.top = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      container.style.justifyContent = 'center';
      container.style.background = 'rgba(255,255,255,0.6)';
      container.style.zIndex = '9999';

      const spinner = document.createElement('div');
      spinner.className = 'nfy-spinner';
      spinner.setAttribute('aria-hidden', 'true');

      container.appendChild(spinner);

      // Append to body so it's visible on top of the page
      document.body.appendChild(container);
    } catch (e) {
      // don't block on DOM errors
      _apiClientLog.warn('_showLoader failed', e);
    }
  }

  /**
   * Hide the loader
   */
  _hideLoader() {
    try {
      const existing = document.getElementById('cart-backorder-loader');
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    } catch (e) {
      _apiClientLog.warn('_hideLoader failed', e);
    }
  }

  /**
   * Check if cached data is still valid
   * @param {string} cacheKey - The cache key to check
   * @returns {boolean} - True if cache is valid, false otherwise
   */
  isCacheValid(cacheKey) {
    if (!this.cacheEnabled) return false;
    if (!this.cache.has(cacheKey)) return false;

    const timestamp = this.cacheTimestamps.get(cacheKey);
    const isValid = Date.now() - timestamp < this.cacheDuration;

    if (!isValid) {
      this.cache.delete(cacheKey);
      this.cacheTimestamps.delete(cacheKey);
    }

    return isValid;
  }

  /**
   * Generate cache key from SKU array
   * @param {string[]} skuArray - Array of SKUs
   * @returns {string} - Cache key
   */
  generateCacheKey(skuArray) {
    return `stock_data_${skuArray.sort().join('_')}`;
  }

  /**
   * Fetch stock data from API
   * @param {string[]} skuArray - Array of product SKUs
   * @returns {Promise<Map>} - Map of SKU to stock data { in_stock_quantity, pending_ordered_quantity }
   */
  async fetchStockData(skuArray) {
    _apiClientLog.log('post data-------------------', skuArray);
    if (!Array.isArray(skuArray) || skuArray.length === 0) {
      _apiClientLog.warn('Invalid SKU array provided');
      return new Map();
    }

    // Filter out empty/null SKUs
    const validSkus = skuArray.filter((sku) => sku && sku.trim());

    if (validSkus.length === 0) {
      _apiClientLog.warn('No valid SKUs after filtering');
      return new Map();
    }

    // Check cache first
    const cacheKey = this.generateCacheKey(validSkus);
    if (this.isCacheValid(cacheKey)) {
      _apiClientLog.log('Returning cached stock data');
      return this.cache.get(cacheKey);
    }

    // show loader while fetching
    this._showLoader();
    try {
      _apiClientLog.log('Fetching stock data for SKUs:', validSkus);

      const response = await fetch(this.baseUrl + this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          product_codes: validSkus
        })
      });

      if (!response.ok) {
        throw new Error(`API responded with status ${response.status}`);
      }

      const responseData = await response.json();
      _apiClientLog.log('Stock data response after JSON parsing:', responseData);
      // Save last raw response for debugging
      this.lastResponse = responseData;
      if (!responseData.status) {
        throw new Error(responseData.message || 'API returned error status');
      }

      // Transform API response to Map keyed by SKU (product_code)
      const stockDataMap = new Map();

      if (Array.isArray(responseData.data)) {
        responseData.data.forEach((item) => {
          if (item.product_code) {
            stockDataMap.set(item.product_code, {
              id: item.id,
              product_code: item.product_code,
              in_stock_quantity: parseInt(item.in_stock_quantity, 10) || 0,
              pending_ordered_quantity: parseInt(item.pending_ordered_quantity, 10) || 0,
              // include API-provided production fields for calculations
              lead_time_in_days: item.lead_time_in_days != null ? parseInt(item.lead_time_in_days, 10) || 0 : 0,
              daily_production_capacity:
                item.daily_production_capacity != null ? parseInt(item.daily_production_capacity, 10) || 0 : 0,
              manufacturing_type_name: item.manufacturing_type_name || '',
              manufacturing_type_id:
                item.manufacturing_type_id != null ? parseInt(item.manufacturing_type_id, 10) || 0 : 0,
              // include HS code and country of origin for duty calculations
              hs_code: item.hs_code ? String(item.hs_code).trim() : null,
              country_of_origin: item.country_of_origin ? String(item.country_of_origin).trim().toUpperCase() : null
            });
          }
        });
      }

      // Cache the result
      if (this.cacheEnabled) {
        this.cache.set(cacheKey, stockDataMap);
        this.cacheTimestamps.set(cacheKey, Date.now());
      }

      // Expose stock data globally for duty calculator
      this.exposeStockDataGlobally(stockDataMap);

      _apiClientLog.log('Stock data fetched successfully:', stockDataMap);
      return stockDataMap;
    } catch (error) {
      _apiClientLog.error('Failed to fetch stock data:', error);
      return new Map(); // Return empty map on error
    } finally {
      // always hide loader when done
      this._hideLoader();
    }
  }

  /**
   * Expose stock data globally for duty calculator
   * Creates window.CartApiStockData with HS codes and country of origin
   * @param {Map} stockDataMap - Stock data map from API
   */
  exposeStockDataGlobally(stockDataMap) {
    if (!stockDataMap || stockDataMap.size === 0) {
      _apiClientLog.warn('No stock data to expose globally');
      return;
    }

    window.CartApiStockData = {
      timestamp: Date.now(),
      products: {}
    };

    stockDataMap.forEach((data, sku) => {
      window.CartApiStockData.products[sku] = {
        hs_code: data.hs_code || null,
        country_of_origin: data.country_of_origin || null,
        in_stock_quantity: data.in_stock_quantity || 0,
        pending_ordered_quantity: data.pending_ordered_quantity || 0,
        lead_time_in_days: data.lead_time_in_days || 0,
        daily_production_capacity: data.daily_production_capacity || 0,
        manufacturing_type_name: data.manufacturing_type_name || '',
        manufacturing_type_id: data.manufacturing_type_id || 0
      };
    });

    _apiClientLog.log('Stock data exposed globally:', window.CartApiStockData);

    // Dispatch custom event to notify cart totals calculator that API data is ready
    window.dispatchEvent(
      new CustomEvent('cart-api-stock-data-ready', {
        detail: window.CartApiStockData
      })
    );
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
    this.cacheTimestamps.clear();
    _apiClientLog.log('Cache cleared');
  }

  /**
   * Disable/enable cache
   * @param {boolean} enabled - Cache enabled state
   */
  setCacheEnabled(enabled) {
    this.cacheEnabled = enabled;
  }

  /**
   * Create a draft order for B2B customers
   * @param {Array} lineItems - Array of line items with title, quantity, price, requires_shipping, sku
   * @param {string} email - Customer email address
   * @param {Object} shippingAddress - Shipping address object { first_name, last_name, address1, city, country, zip }
   * @param {Object} shippingLine - Shipping line object { title, price, custom }
   * @param {boolean} taxExempt - Whether the order is tax exempt
   * @returns {Promise<Object>} - API response with invoice_url or error
   */
  async createDraftOrder(email, vat, lineItems, shippingAddress, shippingLine = null, taxExempt = true) {
    _apiClientLog.log('Creating draft order for email:', email);
    const apiUrlDraftOrder = this.baseUrl + 'v1/shopify-draft-order/create';

    // Validate inputs
    if (!Array.isArray(lineItems) || lineItems.length === 0) {
      _apiClientLog.error('Invalid line items provided');
      throw new Error('Line items are required and must be an array');
    }

    if (!email || typeof email !== 'string') {
      _apiClientLog.error('Invalid email provided');
      throw new Error('Valid email is required');
    }
    if (!vat || typeof vat !== 'string') {
      _apiClientLog.error('Invalid VAT provided');
      throw new Error('Valid VAT is required');
    }

    if (!shippingAddress || typeof shippingAddress !== 'object') {
      _apiClientLog.error('Invalid shipping address provided');
      throw new Error('Valid shipping address is required');
    }

    // Build the request payload
    const payload = {
      draft_order: {
        email: email,
        vat: vat,
        tax_exempt: taxExempt,
        line_items: lineItems.map((item) => ({
          title: item.title,
          quantity: item.quantity,
          price: item.price,
          requires_shipping: item.requires_shipping,
          sku: item.sku,
          applied_discount: item.applied_discount
        })),
        shipping_address: {
          country: shippingAddress.country
        },
        use_customer_default_address: false
      }
    };

    // Add shipping line if provided
    if (shippingLine && typeof shippingLine === 'object') {
      payload.draft_order.shipping_line = {
        title: shippingLine.title || '',
        price: shippingLine.price || '0.00',
        custom: true
      };
    }

    _apiClientLog.log('Draft order payload:', payload);

    // show loader while fetching
    this._showLoader();
    try {
      const response = await fetch(apiUrlDraftOrder, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        _apiClientLog.error('Draft order API responded with status:', response);
        throw new Error(`API responded with status ${response.status}`);
      }

      const responseData = await response.json();
      _apiClientLog.log('Draft order response:', responseData);

      if (!responseData.status) {
        throw new Error(responseData.message || 'API returned error status');
      }

      return responseData;
    } catch (error) {
      _apiClientLog.error('Failed to create draft order:', error);
      throw error;
    } finally {
      // always hide loader when done
      this._hideLoader();
    }
  }

  /**
   * Save customer B2B VAT information to backend
   * @param {Object} customerData - Customer VAT data
   * @param {string} customerData.name - Customer business name
   * @param {string} customerData.email - Customer business email
   * @param {string} customerData.vat - VAT number
   * @param {string} customerData.vatCountry - VAT country
   * @param {boolean} [customerData.isVerified=false] - Whether VAT is verified
   * @returns {Promise<Object>} - API response { status: boolean, message: string }
   */
  async saveCustomerVATInfo(customerData) {
    const apiUrl = this.baseUrl + 'v1/shopify-customer/vat-exemptions';

    // Validate required fields
    if (!customerData.name || !customerData.email || !customerData.vat || !customerData.vatCountry) {
      throw new Error('Missing required customer data: name, email, vat, and vatCountry are required');
    }

    // Build the request payload
    const payload = {
      name: customerData.name,
      email: customerData.email,
      vats: [
        {
          vat: customerData.vat,
          vat_country: customerData.vatCountry,
          is_verified: customerData.isVerified || false
        }
      ]
    };

    _apiClientLog.log('Saving customer VAT info:', payload);

    // Show loader while fetching
    this._showLoader();

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        _apiClientLog.error('VAT save API responded with status:', response.status);
        throw new Error(`API responded with status ${response.status}`);
      }

      const responseData = await response.json();
      _apiClientLog.log('VAT save response:', responseData);

      if (!responseData.status) {
        throw new Error(responseData.message || 'Failed to save customer VAT information');
      }

      return responseData;
    } catch (error) {
      _apiClientLog.error('Failed to save customer VAT info:', error);
      throw error;
    } finally {
      // Always hide loader when done
      this._hideLoader();
    }
  }
}

// Create global instance
window.externalApiClient = new ExternalApiClient({
  cacheEnabled: true,
  cacheDuration: 5 * 60 * 1000 // 5 minutes
});

_apiClientLog.log('Initialized successfully');










// /**
//  * External API Client
//  * Fetches stock data from backend API using product SKUs
//  *
//  * API Endpoint: https://nets4you-stock-node-api-stage.onrender.com/api/v1/products/getProductInStockQtyAndPendingOrderCount
//  * Producttion:
//  * https://nets4you-stock-node-api-production.onrender.com/api/v1/products/getProductInStockQtyAndPendingOrderCount
//  * Request Body: { "product_ids": ["SKU1", "SKU2", ...] }
//  * Response: { status: true, message: "Success", data: [...] }
//  */

// class ExternalApiClient {
//   constructor(options = {}) {
//     this.apiEndpoint =
//       'https://nets4you-stock-node-api-stage.onrender.com/api/v1/products/getProductInStockQtyAndPendingOrderCount';
//     this.cacheEnabled = options.cacheEnabled !== false;
//     this.cacheDuration = options.cacheDuration || 5 * 60 * 1000; // 5 minutes default
//     this.cache = new Map();
//     this.cacheTimestamps = new Map();
//   }

//   /**
//    * Show a simple page-level loader while API requests are in progress
//    */
//   _showLoader() {
//     try {
//       const existing = document.getElementById('cart-backorder-loader');
//       if (existing) return;

//       // Inject spinner keyframes/style if needed
//       if (!document.getElementById('cart-backorder-loader-style')) {
//         const style = document.createElement('style');
//         style.id = 'cart-backorder-loader-style';
//         style.textContent = `
//           @keyframes nfy-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
//           .nfy-spinner { width: 40px; height: 40px; border: 4px solid rgba(0,0,0,0.08); border-top-color: #111827; border-radius: 50%; animation: nfy-spin 1s linear infinite; }
//         `;
//         document.head.appendChild(style);
//       }

//       const container = document.createElement('div');
//       container.id = 'cart-backorder-loader';
//       container.setAttribute('aria-hidden', 'false');
//       container.style.position = 'fixed';
//       container.style.left = '0';
//       container.style.top = '0';
//       container.style.width = '100%';
//       container.style.height = '100%';
//       container.style.display = 'flex';
//       container.style.alignItems = 'center';
//       container.style.justifyContent = 'center';
//       container.style.background = 'rgba(255,255,255,0.6)';
//       container.style.zIndex = '9999';

//       const spinner = document.createElement('div');
//       spinner.className = 'nfy-spinner';
//       spinner.setAttribute('aria-hidden', 'true');

//       container.appendChild(spinner);

//       // Append to body so it's visible on top of the page
//       document.body.appendChild(container);
//     } catch (e) {
//       // don't block on DOM errors
//       console.warn('[ExternalApiClient] _showLoader failed', e);
//     }
//   }

//   /**
//    * Hide the loader
//    */
//   _hideLoader() {
//     try {
//       const existing = document.getElementById('cart-backorder-loader');
//       if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
//     } catch (e) {
//       console.warn('[ExternalApiClient] _hideLoader failed', e);
//     }
//   }

//   /**
//    * Check if cached data is still valid
//    * @param {string} cacheKey - The cache key to check
//    * @returns {boolean} - True if cache is valid, false otherwise
//    */
//   isCacheValid(cacheKey) {
//     if (!this.cacheEnabled) return false;
//     if (!this.cache.has(cacheKey)) return false;

//     const timestamp = this.cacheTimestamps.get(cacheKey);
//     const isValid = Date.now() - timestamp < this.cacheDuration;

//     if (!isValid) {
//       this.cache.delete(cacheKey);
//       this.cacheTimestamps.delete(cacheKey);
//     }

//     return isValid;
//   }

//   /**
//    * Generate cache key from SKU array
//    * @param {string[]} skuArray - Array of SKUs
//    * @returns {string} - Cache key
//    */
//   generateCacheKey(skuArray) {
//     return `stock_data_${skuArray.sort().join('_')}`;
//   }

//   /**
//    * Fetch stock data from API
//    * @param {string[]} skuArray - Array of product SKUs
//    * @returns {Promise<Map>} - Map of SKU to stock data { in_stock_quantity, pending_ordered_quantity }
//    */
//   async fetchStockData(skuArray) {
//     console.log('[ExternalApiClient] post data-------------------', skuArray);
//     if (!Array.isArray(skuArray) || skuArray.length === 0) {
//       console.warn('[ExternalApiClient] Invalid SKU array provided');
//       return new Map();
//     }

//     // Filter out empty/null SKUs
//     const validSkus = skuArray.filter((sku) => sku && sku.trim());

//     if (validSkus.length === 0) {
//       console.warn('[ExternalApiClient] No valid SKUs after filtering');
//       return new Map();
//     }

//     // Check cache first
//     const cacheKey = this.generateCacheKey(validSkus);
//     if (this.isCacheValid(cacheKey)) {
//       console.log('[ExternalApiClient] Returning cached stock data');
//       return this.cache.get(cacheKey);
//     }

//     // show loader while fetching
//     this._showLoader();
//     try {
//       console.log('[ExternalApiClient] Fetching stock data for SKUs:', validSkus);

//       const response = await fetch(this.apiEndpoint, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json'
//         },
//         body: JSON.stringify({
//           product_codes: validSkus
//         })
//       });

//       if (!response.ok) {
//         throw new Error(`API responded with status ${response.status}`);
//       }

//       const responseData = await response.json();
//       console.log('[ExternalApiClient] Stock data response after JSON parsing:', responseData);
//       // Save last raw response for debugging
//       this.lastResponse = responseData;
//       if (!responseData.status) {
//         throw new Error(responseData.message || 'API returned error status');
//       }

//       // Transform API response to Map keyed by SKU (product_code)
//       const stockDataMap = new Map();

//       if (Array.isArray(responseData.data)) {
//         responseData.data.forEach((item) => {
//           if (item.product_code) {
//             stockDataMap.set(item.product_code, {
//               id: item.id,
//               product_code: item.product_code,
//               in_stock_quantity: parseInt(item.in_stock_quantity, 10) || 0,
//               pending_ordered_quantity: parseInt(item.pending_ordered_quantity, 10) || 0,
//               // include API-provided production fields for calculations
//               lead_time_in_days: item.lead_time_in_days != null ? parseInt(item.lead_time_in_days, 10) || 0 : 0,
//               daily_production_capacity:
//                 item.daily_production_capacity != null ? parseInt(item.daily_production_capacity, 10) || 0 : 0,
//               manufacturing_type_name: item.manufacturing_type_name || '',
//               manufacturing_type_id:
//                 item.manufacturing_type_id != null ? parseInt(item.manufacturing_type_id, 10) || 0 : 0,
//               // include HS code and country of origin for duty calculations
//               hs_code: item.hs_code ? String(item.hs_code).trim() : null,
//               country_of_origin: item.country_of_origin ? String(item.country_of_origin).trim().toUpperCase() : null
//             });
//           }
//         });
//       }

//       // Cache the result
//       if (this.cacheEnabled) {
//         this.cache.set(cacheKey, stockDataMap);
//         this.cacheTimestamps.set(cacheKey, Date.now());
//       }

//       // Expose stock data globally for duty calculator
//       this.exposeStockDataGlobally(stockDataMap);

//       console.log('[ExternalApiClient] Stock data fetched successfully:', stockDataMap);
//       return stockDataMap;
//     } catch (error) {
//       console.error('[ExternalApiClient] Failed to fetch stock data:', error);
//       return new Map(); // Return empty map on error
//     } finally {
//       // always hide loader when done
//       this._hideLoader();
//     }
//   }

//   /**
//    * Expose stock data globally for duty calculator
//    * Creates window.CartApiStockData with HS codes and country of origin
//    * @param {Map} stockDataMap - Stock data map from API
//    */
//   exposeStockDataGlobally(stockDataMap) {
//     if (!stockDataMap || stockDataMap.size === 0) {
//       console.warn('[ExternalApiClient] No stock data to expose globally');
//       return;
//     }

//     window.CartApiStockData = {
//       timestamp: Date.now(),
//       products: {}
//     };

//     stockDataMap.forEach((data, sku) => {
//       window.CartApiStockData.products[sku] = {
//         hs_code: data.hs_code || null,
//         country_of_origin: data.country_of_origin || null,
//         in_stock_quantity: data.in_stock_quantity || 0,
//         pending_ordered_quantity: data.pending_ordered_quantity || 0,
//         lead_time_in_days: data.lead_time_in_days || 0,
//         daily_production_capacity: data.daily_production_capacity || 0,
//         manufacturing_type_name: data.manufacturing_type_name || '',
//         manufacturing_type_id: data.manufacturing_type_id || 0
//       };
//     });

//     console.log('[ExternalApiClient] Stock data exposed globally:', window.CartApiStockData);

//     // Dispatch custom event to notify cart totals calculator that API data is ready
//     window.dispatchEvent(
//       new CustomEvent('cart-api-stock-data-ready', {
//         detail: window.CartApiStockData
//       })
//     );
//   }

//   /**
//    * Clear cache
//    */
//   clearCache() {
//     this.cache.clear();
//     this.cacheTimestamps.clear();
//     console.log('[ExternalApiClient] Cache cleared');
//   }

//   /**
//    * Disable/enable cache
//    * @param {boolean} enabled - Cache enabled state
//    */
//   setCacheEnabled(enabled) {
//     this.cacheEnabled = enabled;
//   }
// }

// // Create global instance
// window.externalApiClient = new ExternalApiClient({
//   cacheEnabled: true,
//   cacheDuration: 5 * 60 * 1000 // 5 minutes
// });

// console.log('[ExternalApiClient] Initialized successfully');
